import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Invoice } from '../types';
import { invoiceAPI } from '../services/api';

const STATUS_OPTIONS: { value: Invoice['status']; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
];

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getAll();
      setInvoices(response.data.results);
      setError(null);
    } catch (err) {
      setError('Failed to fetch invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await invoiceAPI.delete(id);
        fetchInvoices();
      } catch (err) {
        setError('Failed to delete invoice');
        console.error(err);
      }
    }
  };

  const handleStatusChange = async (invoice: Invoice, newStatus: Invoice['status']) => {
    if (invoice.status === newStatus) return;
    
    setUpdatingStatus(invoice.id!);
    try {
      // Only send status - don't send line_items to preserve time entry links
      await invoiceAPI.patch(invoice.id!, { status: newStatus });
      // Update local state immediately for better UX
      setInvoices(prev => prev.map(inv => 
        inv.id === invoice.id ? { ...inv, status: newStatus } : inv
      ));
      setError(null);
    } catch (err) {
      setError('Failed to update invoice status');
      console.error(err);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T00:00:00').toLocaleDateString();
  };

  const formatBillingPeriod = (startDate: string, endDate: string) => {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    
    // Format as "MMM D - MMM D, YYYY" or "MMM D - D, YYYY" if same month
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endDay = end.getDate();
    const year = end.getFullYear();
    
    if (startMonth === endMonth && start.getFullYear() === end.getFullYear()) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Check if an invoice is overdue based on due_date (for sent invoices)
  const isOverdue = (invoice: Invoice): boolean => {
    if (invoice.status === 'paid' || invoice.status === 'draft') return false;
    if (invoice.status === 'overdue') return true; // Already marked overdue
    // Check if due_date has passed for sent invoices
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(invoice.due_date + 'T00:00:00');
    return dueDate < today;
  };

  // Compute invoice totals
  const outstandingInvoices = invoices.filter(inv => inv.status !== 'paid');
  const overdueInvoices = outstandingInvoices.filter(inv => isOverdue(inv));
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');

  const totalOutstanding = outstandingInvoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);
  const totalOverdue = overdueInvoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);
  const totalPaid = paidInvoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);

  if (loading) return <div className="loading">Loading invoices...</div>;

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Invoices</h2>
          <Link to="/invoices/new" className="btn btn-primary">
            Create New Invoice
          </Link>
        </div>
        
        {error && <div className="error">{error}</div>}
        
        {invoices.length === 0 ? (
          <p>No invoices found. Create your first invoice to get started.</p>
        ) : (
          <>
            {/* Unpaid Invoices Section */}
            {outstandingInvoices.length > 0 && (
              <>
                <h3 style={{ 
                  marginTop: '1rem', 
                  marginBottom: '0.75rem', 
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ 
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#e74c3c'
                  }}></span>
                  Outstanding
                </h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                  gap: '1rem',
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e1e8ed'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50' }}>
                      {formatCurrency(totalOutstanding)}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Outstanding</div>
                  </div>
                  {totalOverdue > 0 && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e74c3c' }}>
                        {formatCurrency(totalOverdue)}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>Overdue</div>
                    </div>
                  )}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#7f8c8d' }}>
                      {outstandingInvoices.length}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Invoices</div>
                  </div>
                </div>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>PO Number</th>
                        <th>Company</th>
                        <th>Billing Period</th>
                        <th>Issue Date</th>
                        <th>Due Date</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstandingInvoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td>{invoice.invoice_number}</td>
                          <td>{invoice.po_details?.po_number || `PO #${invoice.purchase_order}`}</td>
                          <td>{invoice.customer_details?.name}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {formatBillingPeriod(invoice.start_date, invoice.end_date)}
                          </td>
                          <td>{formatDate(invoice.issue_date)}</td>
                          <td>{formatDate(invoice.due_date)}</td>
                          <td>{formatCurrency(invoice.total_amount || 0)}</td>
                          <td>
                            <select
                              value={invoice.status}
                              onChange={(e) => handleStatusChange(invoice, e.target.value as Invoice['status'])}
                              disabled={updatingStatus === invoice.id}
                              className={`status-select status-${invoice.status}`}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                cursor: updatingStatus === invoice.id ? 'wait' : 'pointer',
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                backgroundColor: isOverdue(invoice) ? '#f8d7da' :
                                                invoice.status === 'sent' ? '#cce5ff' : '#fff3cd',
                                color: isOverdue(invoice) ? '#721c24' :
                                      invoice.status === 'sent' ? '#004085' : '#856404',
                              }}
                            >
                              {STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            {isOverdue(invoice) && invoice.status !== 'overdue' && (
                              <div style={{ fontSize: '0.7rem', color: '#721c24', marginTop: '2px' }}>
                                Past due
                              </div>
                            )}
                          </td>
                          <td className="actions">
                            <Link to={`/invoices/${invoice.id}`} className="btn btn-primary btn-small">
                              View
                            </Link>
                            <Link
                              to={`/invoices/${invoice.id}/edit`}
                              className="btn btn-secondary btn-small"
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => handleDelete(invoice.id!)}
                              className="btn btn-danger btn-small"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Paid Invoices Section */}
            {paidInvoices.length > 0 && (
              <>
                <h3 style={{ 
                  marginTop: '2rem', 
                  marginBottom: '0.75rem', 
                  color: '#27ae60',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ 
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#27ae60'
                  }}></span>
                  Paid
                </h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                  gap: '1rem',
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e1e8ed'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#27ae60' }}>
                      {formatCurrency(totalPaid)}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Paid</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#7f8c8d' }}>
                      {paidInvoices.length}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Invoices</div>
                  </div>
                </div>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>PO Number</th>
                        <th>Company</th>
                        <th>Billing Period</th>
                        <th>Issue Date</th>
                        <th>Due Date</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidInvoices.map((invoice) => (
                        <tr key={invoice.id} style={{ opacity: 0.75 }}>
                          <td>{invoice.invoice_number}</td>
                          <td>{invoice.po_details?.po_number || `PO #${invoice.purchase_order}`}</td>
                          <td>{invoice.customer_details?.name}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {formatBillingPeriod(invoice.start_date, invoice.end_date)}
                          </td>
                          <td>{formatDate(invoice.issue_date)}</td>
                          <td>{formatDate(invoice.due_date)}</td>
                          <td>{formatCurrency(invoice.total_amount || 0)}</td>
                          <td>
                            <select
                              value={invoice.status}
                              onChange={(e) => handleStatusChange(invoice, e.target.value as Invoice['status'])}
                              disabled={updatingStatus === invoice.id}
                              className={`status-select status-${invoice.status}`}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                cursor: updatingStatus === invoice.id ? 'wait' : 'pointer',
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                backgroundColor: '#d4edda',
                                color: '#155724',
                              }}
                            >
                              {STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="actions">
                            <Link to={`/invoices/${invoice.id}`} className="btn btn-primary btn-small">
                              View
                            </Link>
                            <Link
                              to={`/invoices/${invoice.id}/edit`}
                              className="btn btn-secondary btn-small"
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => handleDelete(invoice.id!)}
                              className="btn btn-danger btn-small"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceList;
